import {
  EditOutlined,
  FormOutlined,
  FileTextOutlined,
  TagsOutlined,
  ExperimentOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { AButton, AInput, AModal, ASelect, ATextArea, ASwitch } from '../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { Card, Col, Form, Row, Space, Tag, Typography, Divider } from 'antd';
import { useToast } from '../../../../hooks/toast';
import { schemaCrmTemplate } from '../../schemas/yup.crm-template.schema';
import {
  ICrmTemplate,
  patchCrmTemplate,
  postCrmTemplate,
  previewCrmTemplate,
} from '../../../../http/crm';

const { Text, Paragraph } = Typography;

interface ITemplateCreateEditProps {
  open: boolean;
  close: () => void;
  template: ICrmTemplate | null;
  setTemplates: React.Dispatch<React.SetStateAction<ICrmTemplate[]>>;
  onSaved?: () => void;
}

const CATEGORY_SUGGESTIONS = [
  'Aniversário',
  'Retorno',
  'Lembrete',
  'Promoção',
  'Pesquisa',
  'Cobrança',
  'Boas-vindas',
];

const extractPlaceholders = (body: string): string[] => {
  const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  const matches: string[] = [];
  let m;
  while ((m = regex.exec(body)) !== null) {
    if (!matches.includes(m[1])) matches.push(m[1]);
  }
  return matches;
};

const TemplateCreateEdit: React.FC<ITemplateCreateEditProps> = ({
  open,
  close,
  template,
  setTemplates,
  onSaved,
}) => {
  const { addToast } = useToast();
  const isEditing = !!template;

  const [name, setName] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [bodyText, setBodyText] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);

  const [renderedPreview, setRenderedPreview] = useState<string>('');
  const [placeholdersPreview, setPlaceholdersPreview] = useState<string[]>([]);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);

  const placeholders = extractPlaceholders(bodyText);

  const resetForm = useCallback(() => {
    setName('');
    setSubject('');
    setBodyText('');
    setCategory('');
    setDescription('');
    setIsActive(true);
    setRenderedPreview('');
    setPlaceholdersPreview([]);
  }, []);

  const handlePreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const payload = {
        name,
        body_text: bodyText,
        category: category || null,
      };
      const res = await previewCrmTemplate(payload);
      setRenderedPreview(res.rendered);
      setPlaceholdersPreview(res.placeholders ?? []);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro no preview',
        description: error?.response?.data?.error ?? 'Erro ao gerar preview.',
      });
    } finally {
      setLoadingPreview(false);
    }
  }, [name, bodyText, category, addToast]);

  const handleCreate = useCallback(async () => {
    const payload = {
      name,
      subject: subject || null,
      body_text: bodyText,
      category: category || null,
      description: description || null,
      placeholders: placeholders.length > 0 ? placeholders : null,
      is_active: true,
    };

    try {
      await schemaCrmTemplate.validate(payload);
      const data = await postCrmTemplate(payload);
      setTemplates((state) => [...state, data]);
      addToast({
        type: 'success',
        title: 'Template criado',
        description: 'Template salvo com sucesso.',
      });
      if (onSaved) onSaved();
      close();
    } catch (err: any) {
      if (err?.name === 'ValidationError') {
        addToast({
          type: 'error',
          title: 'Campos obrigatórios',
          description: err.errors?.[0] ?? 'Verifique os campos.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro ao criar',
          description: err?.response?.data?.error ?? 'Erro desconhecido.',
        });
      }
    }
  }, [
    name,
    subject,
    bodyText,
    category,
    description,
    placeholders,
    setTemplates,
    addToast,
    close,
    onSaved,
  ]);

  const handleEdit = useCallback(async () => {
    if (!template?.id) return;

    const payload = {
      name,
      subject: subject || null,
      body_text: bodyText,
      category: category || null,
      description: description || null,
      placeholders: placeholders.length > 0 ? placeholders : null,
      is_active: isActive,
    };

    try {
      await schemaCrmTemplate.validate(payload);
      const data = await patchCrmTemplate(template.id, payload);
      setTemplates((state) => state.map((e) => (e.id !== data.id ? e : data)));
      addToast({
        type: 'info',
        title: 'Template atualizado',
        description: 'Alterações salvas com sucesso.',
      });
      if (onSaved) onSaved();
      close();
    } catch (err: any) {
      if (err?.name === 'ValidationError') {
        addToast({
          type: 'error',
          title: 'Campos obrigatórios',
          description: err.errors?.[0] ?? 'Verifique os campos.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro ao atualizar',
          description: err?.response?.data?.error ?? 'Erro desconhecido.',
        });
      }
    }
  }, [
    template,
    name,
    subject,
    bodyText,
    category,
    description,
    placeholders,
    isActive,
    setTemplates,
    addToast,
    close,
    onSaved,
  ]);

  useEffect(() => {
    if (!open) return;

    if (template) {
      setName(template.name ?? '');
      setSubject((template as any)?.subject ?? '');
      setBodyText(template.body_text ?? '');
      setCategory(template.category ?? '');
      setDescription(template.description ?? '');
      setIsActive(!!template.is_active);
      return;
    }

    resetForm();
  }, [open, template, resetForm]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        isEditing ? (
          <>
            <EditOutlined /> Editar Template
          </>
        ) : (
          <>
            <FormOutlined /> Novo Template de Mensagem
          </>
        )
      }
      onOk={isEditing ? handleEdit : handleCreate}
      width={780}
    >
      <Form layout="vertical" style={{ maxWidth: '100%', marginTop: 8 }}>
        <Card
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space size={8}>
              <FileTextOutlined /> Informações Básicas
            </Space>
          }
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={16}>
              <Form.Item
                label={
                  <Space size={6}>
                    <MessageOutlined style={{ color: '#1890ff' }} /> Nome do template
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Ex: Aniversariantes do mês"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={150}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={
                  <Space size={6}>
                    <TagsOutlined style={{ color: '#2f54eb' }} /> Categoria
                  </Space>
                }
              >
                <ASelect
                  showSearch
                  placeholder="Selecione ou digite"
                  allowClear
                  mode={undefined as any}
                  value={category || undefined}
                  onChange={(v) => setCategory(v ?? '')}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={CATEGORY_SUGGESTIONS.map((c) => ({
                    label: c,
                    value: c,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <FileTextOutlined style={{ color: '#13c2c2' }} /> Assunto
                  </Space>
                }
              >
                <AInput
                  placeholder="Opcional: assunto do e-mail / referência"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <FileTextOutlined style={{ color: '#52c41a' }} /> Descrição
                  </Space>
                }
              >
                <AInput
                  placeholder="Opcional: descrição de uso interno"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space size={8}>
              <MessageOutlined /> Conteúdo do Template
            </Space>
          }
          extra={
            <Space>
              <Tag color="blue" style={{ fontSize: 11 }}>
                Ex: {'\u007b\u007bnome\u007d\u007d'}
              </Tag>
              <Tag color="purple" style={{ fontSize: 11 }}>
                {placeholders.length} placeholders
              </Tag>
            </Space>
          }
        >
          <Row gutter={[12, 0]}>
            <Col xs={24}>
              <Form.Item
                label="Conteúdo (use {'\u007b\u007bvariavel\u007d\u007d'} para campos dinâmicos)"
                required
                style={{ marginBottom: 0 }}
              >
                <ATextArea
                  rows={7}
                  placeholder={
                    'Olá {\u007bnome\u007d}, tudo bem?\n\n' +
                    'Estamos passando para lembrar do seu retorno no dia {\u007bdata\u007d}.\n\n' +
                    'Atenciosamente, Equipe da Clínica.'
                  }
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  maxLength={5000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          {placeholders.length > 0 && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <Row>
                <Col span={24}>
                  <Text strong style={{ fontSize: 12 }}>
                    Placeholders detectados:
                  </Text>
                  <Space wrap size={[6, 6]} style={{ marginLeft: 8 }}>
                    {placeholders.map((p) => (
                      <Tag key={p} color="blue">
                        {'\u007b\u007b'}{p}{'\u007d\u007d'}
                      </Tag>
                    ))}
                  </Space>
                </Col>
              </Row>
            </>
          )}
        </Card>

        <Card
          size="small"
          style={{ marginBottom: isEditing ? 12 : 0 }}
          title={
            <Space size={8}>
              <ExperimentOutlined /> Preview
            </Space>
          }
          extra={
            <Space>
              <Tag color="green" style={{ fontSize: 11 }}>
                {placeholdersPreview.length} extraídos
              </Tag>
              <Space size="small" style={{ fontSize: 11 }}>
                <AButton
                  size="small"
                  icon={<ExperimentOutlined />}
                  loading={loadingPreview}
                  onClick={handlePreview}
                >
                  Gerar preview
                </AButton>
              </Space>
            </Space>
          }
        >
          {renderedPreview ? (
            <div
              style={{
                padding: 12,
                background: '#fafafa',
                borderRadius: 6,
                border: '1px solid #eee',
              }}
            >
              <Paragraph
                style={{
                  whiteSpace: 'pre-wrap',
                  marginBottom: 0,
                  fontFamily: 'monospace',
                  fontSize: 13,
                }}
              >
                {renderedPreview}
              </Paragraph>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Clique em &quot;Gerar preview&quot; para visualizar o template renderizado com um exemplo.
            </Text>
          )}
        </Card>

        {isEditing && (
          <Card size="small">
            <Form.Item label="Status do template" style={{ marginBottom: 0 }}>
              <Space align="center" size={10}>
                <ASwitch
                  checked={isActive}
                  checkedChildren="Ativo"
                  unCheckedChildren="Inativo"
                  onChange={() => setIsActive(!isActive)}
                />
                <Text type="secondary">
                  {isActive
                    ? 'Template disponível para uso em campanhas'
                    : 'Template oculto (não será listado)'}
                </Text>
              </Space>
            </Form.Item>
          </Card>
        )}
      </Form>
    </AModal>
  );
};

export default TemplateCreateEdit;
